/**
 * FILE: App.js
 * PURPOSE: This is the "Main Brain" of the application. 
 * WHAT IT DOES: It handles the navigation (which screen you see), keeps track of your points (IP), 
 * manages whether you are in Dark Mode or Light Mode, and holds all the main data like polls and news.
 * LAYMAN SUMMARY: If the app was a house, this file would be the foundation and the electrical system that connects everything.
 */
import React, { useState, useEffect } from 'react';
import {
  Home, Search, Bell, Wallet, User, Plus,
  Lock, Globe, Flame, Share2, Bookmark,
  X, ChevronRight, CheckCircle2, TrendingUp,
  AlertCircle, ShieldCheck, Settings, Info,
  History, CreditCard, Zap, ShieldAlert,
  ArrowRight, Filter, Eye, Trash2,
  BarChart3, PieChart, Activity, Sun, Moon, Sparkles, Coins, Server
} from 'lucide-react';

import PollCard from './components/PollCard';
import NewsCard from './components/NewsCard';
import { NavItem } from './components/NavItem.js';
import { CreatePollModal } from './components/CreatePollModal.js';
import { ProResearcherDashboard } from './components/ProResearcherDashboard.js';
import { EnrollmentModal } from './components/EnrollmentModal.js';
import { PollInsightsModal } from './components/PollInsightsModal.js';
import SearchModal from './components/SearchModal.js';
import GlobalFeed from './components/GlobalFeed';
import { RewardsModal } from './components/RewardsModal.js';
import { HistoryModal } from './components/HistoryModal.js';
import { AuthProvider, useAuth } from './context/AuthContext';
import { api } from './utils/api';
import PollViewModal from './components/PollViewModal.js';
import { usePulseEngine } from './hooks/usePulseEngine';
import GlobalPulseDrawer from './components/GlobalPulseDrawer';
import { haptics, isHapticsEnabled, setHapticsEnabled } from './utils/haptics';
import { usePWAInstall } from './hooks/usePWAInstall';
import InstallBanner from './components/InstallBanner';
import { DiagnosticVaultScreen } from './components/DiagnosticVaultScreen';
import { AdminDashboard } from './components/AdminDashboard';
import { ToastProvider, useToast } from './context/ToastContext';
import { useModalA11y } from './hooks/useModalA11y';

/**
 * THE INSIGHT ENGINE - PRODUCT GUIDE Summary
 * Main Goal: Share your opinion with total privacy.
 * 
 * HOW IT WORKS (SIMPLE TERMS):
 * 1. TOTAL PRIVACY: 
 *    - No one knows who you are. We use secure tech to keep your identity private.
 *    - Your data is locked on your phone before it's even sent to us.
 * 
 * 2. SPEED: 
 *    - The app does most of the heavy lifting on your phone so it stays fast.
 *    - Global counters update instantly, while detailed charts update every few minutes.
 * 
 * 3. SMART STORAGE: 
 *    - We save space by storing data efficiently.
 *    - Old data that isn't needed anymore is automatically deleted to keep things clean.
 * 
 * 4. HONESTY CHECKS:
 *    - We have simple ways to make sure results are from real, honest people.
 */

// ─── Helpers ────────────────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return "Just now";
  const date = new Date(dateStr);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  return `${Math.floor(diffInSeconds / 86400)}d ago`;
};


/** * SCREEN: LOCKED VAULT (FOR GUESTS)
 */
function LockedVaultScreen({ type, onVerify }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 animate-in fade-in slide-in-from-bottom-8 duration-700 text-center px-4">
      <div className="relative mb-12">
        <div className="absolute inset-0 bg-primary-600/20 blur-[40px] rounded-full animate-pulse"></div>
        <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-8 relative">
          <Lock className="text-primary-600" size={40} />
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white shadow-lg">
            <Zap size={16} />
          </div>
        </div>
      </div>
      <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white mb-6">
        Vault Required
      </h2>
      <p className="text-gray-500 dark:text-gray-400 font-bold italic max-w-sm mb-12 leading-relaxed text-sm">
        The <span className="text-primary-600">{type}</span> section requires a verified Ghost Identity to access. Establishing one also syncs your rewards and verifies your vote integrity.
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={onVerify}
          className="px-10 py-5 bg-primary-600 hover:bg-primary-700 text-white rounded-[24px] font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-purple-900/40 transition-all hover:scale-105 active:scale-95 flex items-center gap-3"
        >
          <ShieldCheck size={18} />
          Establish Vault
        </button>
        <button
          onClick={() => window.open('/faq', '_blank')}
          className="px-8 py-5 bg-white dark:bg-gray-800 text-gray-500 hover:text-gray-900 dark:hover:text-white rounded-[24px] font-black uppercase tracking-[0.2em] text-2xs border border-gray-100 dark:border-gray-700 transition-all"
        >
          Learn About Anonymity
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { isVerified, anonymousId, login, logout } = useAuth();
  const { showToast } = useToast();
  const { onlineTotal, pollExposure, pollUpdates, registerVisibility, pulseId, lastSync, status, serverTime } = usePulseEngine(anonymousId);
  // Navigation State
  const [currentScreen, setCurrentScreen] = useState(() => {
    const path = window.location.pathname.toLowerCase().trim();
    return (path === '/admin' || path.startsWith('/admin/')) ? 'admin' : 'feed';
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isEnrollmentOpen, setIsEnrollmentOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isRewardsModalOpen, setIsRewardsModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [pollToView, setPollToView] = useState(null);
  const [pollToViewSelection, setPollToViewSelection] = useState(null);
  const [isPulseDrawerOpen, setIsPulseDrawerOpen] = useState(false);
  const { isInstallable, isMobile, handleInstall, setIsInstallable } = usePWAInstall();
  const [isInstallDismissed, setIsInstallDismissed] = useState(() => {
    const saved = localStorage.getItem('pwa_install_dismissed');
    if (!saved) return false;
    // Expire dismissal after 24 hours
    const now = Date.now();
    if (now - parseInt(saved) > 86400000) {
      localStorage.removeItem('pwa_install_dismissed');
      return false;
    }
    return true;
  });
  const [pendingVote, setPendingVote] = useState(() => {
    const saved = localStorage.getItem('pending_vote');
    return saved ? JSON.parse(saved) : null;
  });

  // App Logic States
  const [isCalculating, setIsCalculating] = useState(false);
  const [ipBalance, setIpBalance] = useState(0);
  const [streak, setStreak] = useState(0);
  const [votedPolls, setVotedPolls] = useState(() => {
    const saved = localStorage.getItem('votedPolls');
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedCategories, setSelectedCategories] = useState(['All']);
  const [insightsModalPoll, setInsightsModalPoll] = useState(null);
  const [newsData, setNewsData] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  const [votedPollsList, setVotedPollsList] = useState([]);
  const [myPollsList, setMyPollsList] = useState([]);
  const [savedPollsList, setSavedPollsList] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [highlightedPollId, setHighlightedPollId] = useState(null);

  // Global Actions
  // handleVote is the single entry point for casting a vote, called from PollCard (via
  // onVote) regardless of which screen/modal the poll is shown in. It is passed down through
  // many layers of props rather than through context, so any change to its signature ripples
  // through GlobalFeed, PollViewModal, SavedPollsScreen, VotedPollsScreen, MyPollsScreen, etc.
  const handleVote = React.useCallback(async (pollId, optionId, biometrics = {}) => {
    if (!isVerified) {
      // Guest attempted to vote: remember the intended vote and prompt enrollment. Once
      // isVerified flips true, the effect below (watching isVerified && pendingVote) replays
      // this automatically by calling handleVote again with the same args.
      const pendingData = { pollId, optionId, biometrics };
      setPendingVote(pendingData);
      localStorage.setItem('pending_vote', JSON.stringify(pendingData));
      setIsEnrollmentOpen(true);
      return;
    }

    if (!pollId) return;

    setVotedPolls(prev => ({ ...prev, [pollId]: 'processing' }));
    setIsCalculating(true);

    try {
      // Cast the vote to the backend
      const result = await api.castVote(pollId, optionId, anonymousId, biometrics);

      // Update local state after successful vote
      setVotedPolls(prev => ({ ...prev, [pollId]: 'revealed' }));

      // NEW: Dynamically update the Voted History list so researchers/users see it immediately without refresh
      // NEW: Dynamically update the Voted History list so researchers/users see it immediately without refresh
      // Check multiple sources for the poll object to ensure it's added to history even from direct links
      const votedPoll = feedPolls.find(p => p.id === pollId) || (pollToView?.id === pollId ? pollToView : null);
      
      if (votedPoll) {
        const entry = { 
          ...votedPoll, 
          id: votedPoll.id || pollId, // Ensure ID is present
          voted_at: new Date().toISOString() 
        };
        setVotedPollsList(prev => {
          const list = prev || [];
          // Prevent duplicates
          if (list.some(p => p.id === pollId)) return list;
          return [entry, ...list];
        });
      } else {
        // Fallback: If we can't find the poll object in state, trigger a refresh of the voted list
        api.getVotedPolls(anonymousId).then(data => {
          if (setVotedPollsList) setVotedPollsList(data);
        }).catch(() => {});
      }

      // Increment voter count in local feed for immediate feedback
      setFeedPolls(prev => prev.map(p => {
        if (p.id === pollId) {
          return { ...p, total_voters: (p.total_voters || 0) + 1 };
        }
        return p;
      }));

      // After vote: refresh balance
      try {
        const balanceData = await api.getUserBalance(anonymousId);
        setIpBalance(balanceData.coins || 0);
        setStreak(balanceData.streak || 0);
      } catch (e) {
        setIpBalance(prev => prev + 5);
      }

      setIsCalculating(false);
    } catch (error) {
      console.error("Vote failed:", error);

      // ─── STALE IDENTITY RECOVERY ───
      // If the identity is invalid, the user's local session is out of sync with the Vault (backend).
      // We force a logout and re-enrollment to re-establish the cryptographic link.
      if (error.message.includes("IDENTITY_INVALID")) {
        console.warn("[App] Stale identity detected. Forcing re-authentication.");
        logout();
        localStorage.removeItem('pending_vote');
        localStorage.removeItem('votedPolls');
        setPendingVote(null);
        setVotedPolls({});
        setIsEnrollmentOpen(true);
        showToast("Your Vault session has expired. Please re-establish your Ghost Identity to continue.", 'error');
      }
      // Handle missing resources (like a deleted poll)
      else if (error.message.includes("wasn't found")) {
        localStorage.removeItem('pending_vote');
        setPendingVote(null);
        showToast("This poll is no longer available.", 'error');
      } else {
        showToast("Vote failed: " + error.message, 'error');
      }

      setVotedPolls(prev => ({ ...prev, [pollId]: 'idle' }));
      setIsCalculating(false);
    }
  }, [isVerified, anonymousId, api]); // `api` is a static module import (never changes identity);
  // listing it here is harmless but unnecessary — it does not make this callback re-run on
  // anything `isVerified`/`anonymousId` wouldn't already trigger.

  // Dark mode: Tailwind's `darkMode: 'class'` strategy reads the `dark` class on <html>, so
  // this effect is the ONLY thing that actually applies the theme; `darkMode` state itself
  // is just a boolean flag. Persisted to localStorage['theme'] so it survives reloads. There
  // is no `prefers-color-scheme` / OS-theme detection anywhere — first-time visitors always
  // start in light mode.
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Persist votedPolls to localStorage
  useEffect(() => {
    localStorage.setItem('votedPolls', JSON.stringify(votedPolls));
  }, [votedPolls]);

  useEffect(() => {
    if (pendingVote) localStorage.setItem('pending_vote', JSON.stringify(pendingVote));
    else localStorage.removeItem('pending_vote');
  }, [pendingVote]);

  const [feedPolls, setFeedPolls] = useState([]);
  const [globalStats, setGlobalStats] = useState({ totalVotes: 0, activeGhosts: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const allKnownPolls = React.useMemo(() => {
    const pollMap = new Map();
    feedPolls.forEach(p => pollMap.set(p.id, p));
    (votedPollsList || []).forEach(p => {
        const existing = pollMap.get(p.id);
        pollMap.set(p.id, { ...existing, ...p });
    });
    (myPollsList || []).forEach(p => {
        const existing = pollMap.get(p.id);
        pollMap.set(p.id, { ...existing, ...p });
    });
    (savedPollsList || []).forEach(p => {
        const existing = pollMap.get(p.id);
        pollMap.set(p.id, { ...existing, ...p });
    });
    return Array.from(pollMap.values());
  }, [feedPolls, votedPollsList, myPollsList, savedPollsList]);

    // Fetch Feed and Balance
    useEffect(() => {
        const path = window.location.pathname.toLowerCase().trim();
        if (path === '/admin' || path.startsWith('/admin/')) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
          try {
            setIsLoading(true);
            setPage(1);
            setHasMore(true);

            // ─── Direct Poll Link Handling ───
            const path = window.location.pathname;
            if (path.startsWith('/poll/')) {
              const directPollId = path.split('/poll/')[1];
              if (directPollId) {
                console.log(`[App] Extracting direct poll ID: ${directPollId}`);
                api.getPollById(directPollId).then(p => {
                  if (p) setPollToView(p);
                }).catch(err => {
                  console.warn(`[App] Direct poll fetch failed for ${directPollId}`, err);
                });
              }
            }

        if (anonymousId) {
          // Verify with Ghost context for better filtering
          const [feedData, balanceData, statsData, votedData, newsRes, createdData, bookmarkedDataRaw, notificationsData] = await Promise.all([
            api.getFeed(anonymousId, 1, 12).catch(() => []),
            api.getUserBalance(anonymousId).catch(() => ({ coins: 0, streak: 0 })),
            api.getStats().catch(() => ({ totalVotes: 0, activeGhosts: 0, royaltyEarned: 0 })),
            api.getVotedPolls(anonymousId).catch(() => []),
            api.getNews().catch(() => []),
            api.getCreatedPolls(anonymousId).catch(() => []),
            api.getSavedPolls(anonymousId).catch(() => []),
            api.getNotifications(anonymousId).catch(() => [])
          ]);

          const bookmarkedData = bookmarkedDataRaw.filter(p => p !== null);

          // 1. Sync voted polls synchronously to prevent prop-drill flicker
          const newVoted = { ...votedPolls };
          (votedData || []).forEach(p => {
            newVoted[p.id] = 'revealed';
          });

          setVotedPolls(newVoted);
          setVotedPollsList(votedData);
          setMyPollsList(createdData);
          setSavedPollsList(bookmarkedData);
          setNewsData(newsRes);
          
          if (Array.isArray(notificationsData)) {
            setNotifications(notificationsData);
            setUnreadCount(notificationsData.filter(n => !n.is_read).length);
          }

          // 2. Set feed and user stats
          setFeedPolls(feedData);
          setHasMore(feedData.length === 12);
          setIpBalance(balanceData.coins || 0);
          setStreak(balanceData.streak || 0);
          setGlobalStats(statsData);
        } else {
          // Anonymous mode
          setIpBalance(0);
          setStreak(0);
          setVotedPollsList([]);
          setMyPollsList([]);
          setNotifications([]);
          setUnreadCount(0);

          const [feedData, statsData, newsRes] = await Promise.all([
            api.getFeed(null, 1, 12).catch(() => []),
            api.getStats().catch(() => ({ totalVotes: 0, activeGhosts: 0, royaltyEarned: 0 })),
            api.getNews().catch(() => [])
          ]);
          
          setSavedPollsList([]);

          setFeedPolls(feedData || []);
          setNewsData(newsRes || []);
          setHasMore((feedData || []).length === 12);
          setGlobalStats(statsData || { totalVotes: 0, activeGhosts: 0 });
        }
      } catch (err) {
        console.error("[App] Failed to fetch startup data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [anonymousId]);

  const loadMorePolls = async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      const moreData = await api.getFeed(anonymousId, nextPage, 12);

      if (moreData.length > 0) {
        setFeedPolls(prev => {
          // De-duplicate: only add polls that aren't already in our local feed
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNewPolls = moreData.filter(p => !existingIds.has(p.id));
          return [...prev, ...uniqueNewPolls];
        });
        setPage(nextPage);
        setHasMore(moreData.length === 12);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("[App] Failed to load more polls:", err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Keyboard Shortcuts (Cmd+K / Ctrl+K for search)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for global authentication prompts
  useEffect(() => {
    const handleOpenEnrollment = () => setIsEnrollmentOpen(true);
    window.addEventListener('open-enrollment', handleOpenEnrollment);
    return () => window.removeEventListener('open-enrollment', handleOpenEnrollment);
  }, []);



  // Resume pending vote after verification
  useEffect(() => {
    if (isVerified && pendingVote && pendingVote.pollId) {
      console.log("[App] Resuming pending vote:", pendingVote);
      handleVote(pendingVote.pollId, pendingVote.optionId, pendingVote.biometrics);
      setPendingVote(null);
    }
  }, [isVerified, pendingVote, handleVote]);


  const handleMarkNotificationRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification read:", err);
    }
  };

  // There is no bulk "mark all read" endpoint on the backend, so this fires the existing
  // per-notification call for every unread item. Fine at the current notification volumes;
  // would be worth a dedicated bulk endpoint if that list ever gets large.
  const handleClearAllNotifications = async () => {
    const unread = notifications.filter(n => !n.is_read);
    if (unread.length === 0) return;
    try {
      await Promise.all(unread.map(n => api.markNotificationRead(n.id)));
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to clear notifications:", err);
      showToast("Couldn't clear all notifications — please try again.", 'error');
    }
  };

  const handleBookmarkToggle = async (pollId) => {
    if (!anonymousId) {
      setIsEnrollmentOpen(true);
      return;
    }
    
    // Optimistic Update
    setSavedPollsList(prev => {
      const isSaved = prev.some(p => p.id === pollId);
      if (isSaved) {
        return prev.filter(p => p.id !== pollId);
      } else {
        const pollData = allKnownPolls.find(p => p.id === pollId);
        return pollData ? [...prev, pollData] : prev;
      }
    });

    try {
      await api.toggleSavedPoll(pollId, anonymousId);
    } catch (err) {
      console.error("[App] Failed to toggle bookmark:", err);
      // Revert on failure by re-fetching
      const updatedSaved = await api.getSavedPolls(anonymousId);
      setSavedPollsList(updatedSaved);
      showToast("Couldn't save that poll — please try again.", 'error');
    }
  };

  const handleCreatePoll = async (newPoll) => {
    try {
      setIsCalculating(true);
      const createdPoll = await api.createPoll({
        ...newPoll,
        poll_type: newPoll.type,
        trait_type: 'general',
        creator_id: anonymousId || '',
        duration_hours: newPoll.duration_hours
      });

      // Update local feed with the new poll
      setFeedPolls(prev => [createdPoll, ...prev]);
      setIsCreateModalOpen(false);
      setIsCalculating(false);

      // Spotlight effect: Scroll to top and highlight the new poll
      setHighlightedPollId(createdPoll.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Clear highlight after 5 seconds
      setTimeout(() => setHighlightedPollId(null), 5000);

      console.log("[App] Poll launched successfully:", createdPoll.id);
    } catch (err) {
      console.error("[App] Failed to create poll:", err);
      showToast("Failed to launch poll: " + err.message, 'error');
      setIsCalculating(false);
    }
  };

  // Filter Logic
  const filteredPolls = (selectedCategories.includes('All')
    ? feedPolls
    : feedPolls.filter(p => selectedCategories.includes(p.category)))
    .filter(p => votedPolls[p.id] !== 'revealed');



  const handleCreateClick = () => {
    // Guests can now enter the creation flow. 
    // Login is only required at the final 'Launch' step inside the modal.
    setIsCreateModalOpen(true);
  };

  const handleNextPoll = () => {
    if (!pollToView) return;

    let activePolls = [];
    if (currentScreen === 'feed') activePolls = filteredPolls;
    else if (currentScreen === 'voted') activePolls = votedPollsList;
    else if (currentScreen === 'my_polls') activePolls = myPollsList;
    else if (currentScreen === 'saved') activePolls = savedPollsList;
    else activePolls = filteredPolls;

    const currentIndex = activePolls.findIndex(p => p.id === pollToView.id);
    if (currentIndex !== -1 && currentIndex < activePolls.length - 1) {
      setPollToView(activePolls[currentIndex + 1]);
    }
  };

  const handlePrevPoll = () => {
    if (!pollToView) return;

    let activePolls = [];
    if (currentScreen === 'feed') activePolls = filteredPolls;
    else if (currentScreen === 'voted') activePolls = votedPollsList;
    else if (currentScreen === 'my_polls') activePolls = myPollsList;
    else if (currentScreen === 'saved') activePolls = savedPollsList;
    else activePolls = filteredPolls;

    const currentIndex = activePolls.findIndex(p => p.id === pollToView.id);
    if (currentIndex > 0) {
      setPollToView(activePolls[currentIndex - 1]);
    }
  };

  const completeEnrollment = (authData) => {
    console.log("Vault Established:", authData);
    login(authData.token, authData.anonymousId, authData.email);
    setIsEnrollmentOpen(false);

    // If we were in the middle of creating a poll, re-open that modal
    if (isCreateModalOpen) {
      setIsCreateModalOpen(true);
    }
  };

  // Terminates the local Ghost session and wipes every app-related localStorage key, then
  // resets in-memory state to match a fresh guest session. This is genuinely destructive and
  // irreversible from the user's side (their vote/bookmark history stays on the server tied
  // to the old identity, but this browser has no way back into it), which is why the
  // Preferences screen requires a two-step "arm, then confirm" click before calling this.
  const handleWipeVault = () => {
    const keysToClear = [
      'ghost_token', 'ghost_id', 'ghost_email', 'votedPolls', 'pending_vote',
      'pwa_install_dismissed', 'create_poll_draft_v2', 'x-device-id',
    ];
    keysToClear.forEach(k => localStorage.removeItem(k));
    Object.keys(localStorage)
      .filter(k => k.startsWith('poll_selection_'))
      .forEach(k => localStorage.removeItem(k));

    logout();
    setVotedPolls({});
    setVotedPollsList([]);
    setMyPollsList([]);
    setSavedPollsList([]);
    setIpBalance(0);
    setStreak(0);
    setPendingVote(null);
    setCurrentScreen('feed');
    showToast('Local Vault wiped. You are now a fresh Guest Ghost.', 'success');
  };


  // Navigation Switcher
  const renderScreen = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
          <div className="w-16 h-16 border-4 border-primary-600/20 border-t-primary-600 rounded-full animate-spin mb-4"></div>
          <p className="text-sm font-black text-gray-500 uppercase tracking-widest">Waking up the Brain...</p>
        </div>
      );
    }

    switch (currentScreen) {
      case 'feed':
        return <GlobalFeed
          polls={filteredPolls}
          onVote={handleVote}
          votedPolls={votedPolls}
          newsData={newsData}
          onCreate={handleCreateClick}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          onViewInsights={setInsightsModalPoll}
          anonymousId={anonymousId}
          onSelectPoll={(poll, selection) => {
            setPollToView(poll);
            setPollToViewSelection(selection || null);
          }}
          pollExposure={pollExposure}
          pollUpdates={pollUpdates}
          registerVisibility={registerVisibility}
          serverTime={serverTime}
          onLoadMore={loadMorePolls}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />;
      case 'saved': return isVerified ? <SavedPollsScreen onSelectPoll={(poll, selection) => { setPollToView(poll); setPollToViewSelection(selection || null); }} onViewInsights={setInsightsModalPoll} allPolls={allKnownPolls} setSavedPollsList={setSavedPollsList} votedPolls={votedPolls} onVote={handleVote} anonymousId={anonymousId} pollExposure={pollExposure} pollUpdates={pollUpdates} registerVisibility={registerVisibility} savedPollsList={savedPollsList} onBookmarkToggle={handleBookmarkToggle} /> : <LockedVaultScreen type="Bookmarks" onVerify={() => setIsEnrollmentOpen(true)} />;
      case 'voted': return isVerified ? <VotedPollsScreen onSelectPoll={(poll, selection) => { setPollToView(poll); setPollToViewSelection(selection || null); }} onViewInsights={setInsightsModalPoll} anonymousId={anonymousId} onVote={handleVote} votedMap={votedPolls} votedPollsList={votedPollsList} setVotedPollsList={setVotedPollsList} pollExposure={pollExposure} pollUpdates={pollUpdates} registerVisibility={registerVisibility} savedPollsList={savedPollsList} onBookmarkToggle={handleBookmarkToggle} /> : <LockedVaultScreen type="Voted History" onVerify={() => setIsEnrollmentOpen(true)} />;
      case 'my_polls': return isVerified ? <MyPollsScreen onSelectPoll={(poll, selection) => { setPollToView(poll); setPollToViewSelection(selection || null); }} onViewInsights={setInsightsModalPoll} onCreate={handleCreateClick} anonymousId={anonymousId} allPolls={allKnownPolls} setMyPollsList={setMyPollsList} votedPolls={votedPolls} onVote={handleVote} pollExposure={pollExposure} pollUpdates={pollUpdates} registerVisibility={registerVisibility} savedPollsList={savedPollsList} onBookmarkToggle={handleBookmarkToggle} /> : <LockedVaultScreen type="Creator Tools" onVerify={() => setIsEnrollmentOpen(true)} />;
      case 'diagnostic': return isVerified ? <DiagnosticVaultScreen anonymousId={anonymousId} darkMode={darkMode} /> : <LockedVaultScreen type="Internal Diagnostics" onVerify={() => setIsEnrollmentOpen(true)} />;
      case 'researcher': return <ProResearcherDashboard />;
      case 'settings': return <SettingsScreen darkMode={darkMode} setDarkMode={setDarkMode} onWipeVault={handleWipeVault} />;
      case 'legal': return <LegalScreen />;
      default: return (
        <GlobalFeed 
          polls={filteredPolls} 
          onVote={handleVote} 
          votedPolls={votedPolls} 
          onSelectPoll={(poll, selection) => { setPollToView(poll); setPollToViewSelection(selection || null); }} 
          anonymousId={anonymousId} 
          pollExposure={pollExposure} 
          pollUpdates={pollUpdates} 
          registerVisibility={registerVisibility} 
          savedPollsList={savedPollsList} 
          onBookmarkToggle={handleBookmarkToggle} 
          highlightedPollId={highlightedPollId}
        />
      );
    }
  };

  // Direct-URL admin route: reached by visiting /admin, which sets currentScreen to 'admin'
  // in the initial useState above. This intentionally renders AdminDashboard WITHOUT a
  // Ghost-identity `isVerified` check — AdminDashboard's own username/password login screen
  // is the only gate here. This early return happens before renderScreen() is ever invoked
  // whenever currentScreen is 'admin', so renderScreen()'s switch has no 'admin' case (it
  // would have been unreachable dead code; removed — see audit notes).
  if (currentScreen === 'admin') {
    return (
      <div className={`min-h-screen w-full font-sans selection:bg-primary-600/30 ${darkMode ? 'bg-gray-900 text-white' : 'bg-[#F9FAFB] text-[#1F2937]'}`}>
        <AdminDashboard darkMode={darkMode} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen w-full font-sans selection:bg-primary-600/30 ${darkMode ? 'bg-gray-900 text-white' : 'bg-[#F9FAFB] text-[#1F2937]'}`}>
      <InstallBanner 
        isInstallable={isInstallable && !isInstallDismissed} 
        isMobile={isMobile}
        onInstall={handleInstall} 
        onDismiss={() => {
          localStorage.setItem('pwa_install_dismissed', Date.now().toString());
          setIsInstallDismissed(true);
        }} 
      />

      {/* HEADER: UNIFIED ECONOMY TICKER */}
      <header className={`sticky top-0 z-[100] w-full h-16 md:h-20 border-b px-4 md:px-6 flex items-center justify-between transition-all duration-300 ${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-sm'}`}>
        <div className="flex items-center space-x-2 md:space-x-3 cursor-pointer group flex-shrink-0" onClick={() => { haptics.impact(); setCurrentScreen('feed'); }}>
          <div className="w-8 h-8 md:w-10 md:h-10 bg-primary-600 rounded-lg md:rounded-xl shadow-lg shadow-purple-200 flex items-center justify-center text-white font-extrabold text-lg md:text-xl italic group-hover:scale-110 transition-transform duration-300">E</div>
          <div className="flex flex-col">
            <h1 className="text-sm md:text-xl font-black tracking-tighter uppercase leading-none truncate max-w-[120px] xs:max-w-[140px] md:max-w-none">Open Opinion</h1>
            <span className="hidden sm:block text-2xs font-black text-gray-500 uppercase tracking-[0.2em]">Your Voice, Verified</span>
          </div>
        </div>

        {/* DESKTOP SEARCH BAR TRIGGER */}
        <div 
          onClick={() => { haptics.impact(); setIsSearchOpen(true); }}
          className={`hidden md:flex items-center flex-1 max-w-md mx-8 px-4 h-11 rounded-2xl border cursor-pointer transition-all hover:border-primary-600 hover:shadow-md group/search ${darkMode ? 'bg-gray-800/40 border-gray-700 text-gray-500' : 'bg-gray-50 border-gray-100 text-gray-500'}`}
        >
          <Search size={18} className="mr-3 text-primary-600 group-hover/search:scale-110 transition-transform" />
          <span className="text-2xs md:text-xs font-black uppercase tracking-[0.2em] opacity-70">Search polls or topics...</span>
          <div className="ml-auto flex items-center gap-1 opacity-40 group-hover/search:opacity-100 transition-opacity">
            <kbd className="px-1.5 py-0.5 rounded border border-current text-2xs font-black tabular-nums">⌘</kbd>
            <kbd className="px-1.5 py-0.5 rounded border border-current text-2xs font-black">K</kbd>
          </div>
        </div>

        {/* Global Stats - Mobile Optimized */}
        <div className="flex items-center space-x-1.5 md:space-x-4">
          <button onClick={() => { haptics.impact(); setIsSearchOpen(true); }} className={`p-2 md:hidden rounded-full border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500'}`}>
            <Search size={18} />
          </button>

          <div className={`flex items-center border rounded-full p-0.5 md:p-1 shadow-sm h-8 md:h-11 hover:border-primary-600 transition-all cursor-pointer ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} onClick={() => { haptics.impact(); isVerified ? setIsRewardsModalOpen(true) : setIsEnrollmentOpen(true); }}>
            <div className="flex items-center px-1.5 md:px-4 space-x-1 md:space-x-2 border-r border-gray-100 dark:border-gray-700 h-full">
              <span className={`font-extrabold text-2xs md:text-base ${isVerified ? 'text-primary-600' : 'text-amber-500'}`}>
                {isVerified ? ipBalance.toLocaleString() : 'VERIFY'}
              </span>
            </div>
            <div className="flex items-center px-1.5 md:px-4 space-x-1 md:space-x-2">
              <span className="text-xs md:text-lg">🔥</span>
              <span className="font-extrabold text-2xs md:text-base text-secondary-600">{streak}</span>
            </div>
          </div>

          <button onClick={() => { haptics.impact(); setDarkMode(!darkMode); }} className={`p-2 md:p-2.5 rounded-full border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-yellow-400' : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-600'}`}>
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      <div className="w-full lg:max-w-[1440px] lg:mx-auto flex flex-col lg:flex-row">
        {/* COLUMN 1: NAVIGATION */}
        <aside className={`w-[280px] sticky top-20 h-[calc(100vh-80px)] p-6 hidden lg:flex flex-col border-r ${darkMode ? 'border-gray-800 bg-gray-900/50' : 'border-gray-100 bg-white shadow-sm'}`}>
          <nav className="flex-1 space-y-1">
            <NavItem active={currentScreen === 'feed'} icon={<Home />} label="Global Feed" onClick={() => setCurrentScreen('feed')} darkMode={darkMode} />
            <NavItem active={currentScreen === 'saved'} icon={<Bookmark />} label="Saved" onClick={() => setCurrentScreen('saved')} darkMode={darkMode} isLocked={!isVerified} />
            <NavItem active={currentScreen === 'voted'} icon={<CheckCircle2 />} label="Voted" onClick={() => setCurrentScreen('voted')} darkMode={darkMode} isLocked={!isVerified} />
            <NavItem active={currentScreen === 'my_polls'} icon={<Plus />} label="My Polls" onClick={() => setCurrentScreen('my_polls')} darkMode={darkMode} isLocked={!isVerified} />
            <div className="py-4 px-4 text-xs text-gray-500 italic">
              Tools & Management
            </div>
            {/* No `isPro`/lock badge here — the Pro Dashboard is an open preview page with no
                real paywall behind it, so it shouldn't visually promise a gate it doesn't have. */}
            <NavItem active={currentScreen === 'researcher'} icon={<BarChart3 />} label="Pro Dashboard" onClick={() => setCurrentScreen('researcher')} darkMode={darkMode} />
            <NavItem active={currentScreen === 'settings'} icon={<Settings />} label="Preferences" onClick={() => setCurrentScreen('settings')} darkMode={darkMode} />
            <NavItem active={currentScreen === 'legal'} icon={<Info />} label="Privacy & Security" onClick={() => setCurrentScreen('legal')} darkMode={darkMode} />
            {isVerified && (
              <>
                <div className="py-4 px-4 text-2xs font-black uppercase tracking-widest text-gray-500 border-t border-gray-100 dark:border-gray-800 mt-2">
                  Internal — Developer Only
                </div>
                <NavItem active={currentScreen === 'diagnostic'} icon={<ShieldAlert className={darkMode ? 'text-red-400' : 'text-red-500'} />} label="Internal Diag" onClick={() => setCurrentScreen('diagnostic')} darkMode={darkMode} />
              </>
            )}
          </nav>

          <div className="mt-auto p-6 bg-gray-900 rounded-3xl text-white shadow-2xl relative overflow-hidden group border border-white/5">
            <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:scale-110 transition-transform text-primary-600 rotate-12">
              {isVerified ? <ShieldCheck size={120} /> : <Lock size={120} />}
            </div>
            <div className="relative z-10 w-full">
              {isVerified ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-2xs font-black uppercase tracking-widest text-green-400">
                        Verified Citizen
                      </span>
                    </div>
                    <button
                      onClick={logout}
                      className="p-1 hover:bg-white/10 rounded transition-colors text-white/40 hover:text-red-400"
                      title="Clear Session"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="text-[11px] font-mono opacity-60 mb-1 truncate">{anonymousId}</div>
                  <div className="text-2xs font-mono text-white/40 mb-1 truncate flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <div className={`w-1 h-1 rounded-full ${status === 'online' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                      <span>{status?.toUpperCase() || 'CONNECTING'}</span>
                    </span>
                    {lastSync && <span className="opacity-60 tabular-nums">{lastSync}</span>}
                  </div>
                  <div className="text-2xs font-bold opacity-30 uppercase tracking-tighter">
                    Synchronized Vault
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                      <span className="text-2xs font-black uppercase tracking-widest text-amber-400">
                        Guest Ghost
                      </span>
                    </div>
                    <button
                      onClick={() => setIsEnrollmentOpen(true)}
                      className="text-2xs font-black uppercase bg-amber-400/10 text-amber-400 px-2 py-0.5 rounded border border-amber-400/20 hover:bg-amber-400/20 transition-all"
                    >
                      Verify now
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed mb-5 pr-2">
                    Your session is local. Verify to sync rewards and protect identity.
                  </p>
                  <button
                    onClick={() => setIsEnrollmentOpen(true)}
                    className="w-full py-3 bg-primary-600 hover:bg-primary-700 rounded-2xl text-[11px] font-black uppercase tracking-[0.1em] transition-all shadow-xl shadow-purple-900/40 border border-white/10 flex items-center justify-center gap-2 group/btn"
                  >
                    <ShieldCheck size={14} className="group-hover:scale-110 transition-transform" />
                    Verify Now
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* COLUMN 2: PRIMARY CONTENT */}
        <main className="w-full flex-1 max-w-[620px] lg:mx-auto py-4 md:py-8 px-4 min-h-screen">
          {renderScreen()}
        </main>

        {/* COLUMN 3: GLOBAL PULSE & HISTORY (Persistent) */}
        <aside className={`w-[320px] sticky top-20 h-[calc(100vh-80px)] p-6 hidden xl:flex flex-col border-l ${darkMode ? 'border-gray-800 bg-gray-900/50' : 'border-gray-100 bg-white shadow-sm'}`}>
          <div className="flex-1 overflow-y-auto no-scrollbar space-y-8">
            <section>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xs font-black text-gray-500 uppercase tracking-[0.3em]">Recent Activity</h3>
              </div>
              <div className="space-y-1">
                {votedPollsList.length > 0 ? (
                  votedPollsList.slice(0, 3).map(p => (
                    <HistoryWidget
                      key={p.id}
                      record={{ ...p, title: p.question, date: timeAgo(p.voted_at), icon: "🗳️", action: "Voted" }}
                      onClick={setPollToView}
                      darkMode={darkMode}
                    />
                  ))
                ) : (
                  <div className="p-4 bg-gray-50/50 dark:bg-gray-800/20 rounded-xl border border-dashed border-gray-100 dark:border-gray-700 text-center">
                    <p className="text-2xs font-bold text-gray-500 italic">No recent activity detected.</p>
                  </div>
                )}
                <button
                  onClick={() => setIsHistoryModalOpen(true)}
                  className="w-full mt-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl text-2xs font-black uppercase tracking-widest text-primary-600 transition-all border border-gray-100 dark:border-gray-700/50 shadow-sm"
                >
                  View Full History
                </button>
              </div>
            </section>

            <section>
              <h3 className="text-2xs font-black text-gray-500 uppercase tracking-[0.3em] mb-6">Global Pulse</h3>
              <div className="space-y-6">
                <div className={`p-7 rounded-3xl border shadow-2xl shadow-purple-900/5 group hover:border-primary-600/30 transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-2xs font-black text-gray-500 uppercase tracking-[0.2em]">Network Pulse</span>
                    <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-ping"></span>
                  </div>
                  <div className="text-4xl font-black text-primary-600 tabular-nums tracking-tighter">{(onlineTotal || 1).toLocaleString()}</div>
                  <div className="text-2xs font-bold text-gray-500 uppercase tracking-tight mt-1 opacity-60">Active Citizens Right Now</div>
                </div>

                <div className={`p-7 rounded-3xl border shadow-2xl shadow-purple-900/5 group hover:border-primary-600/30 transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-2xs font-black text-gray-500 uppercase tracking-[0.2em]">Your Balance</span>
                    {isVerified ? (
                      <Coins size={16} className="text-primary-600" />
                    ) : (
                      <Lock size={16} className="text-gray-500" />
                    )}
                  </div>
                  <div className="text-4xl font-black text-primary-600 tabular-nums tracking-tighter">
                    {isVerified ? ipBalance.toLocaleString() : '0'}
                    <span className="text-xs uppercase ml-3 text-gray-500 tracking-widest font-bold">Coins</span>
                  </div>
                  <div className="text-2xs font-bold text-gray-500 uppercase tracking-tight mt-1 opacity-60">
                    {isVerified ? 'Synchronized Ledger Total' : 'Session establishes on verification'}
                  </div>
                </div>


                {(() => {
                  const trendingPoll = [...feedPolls].sort((a, b) => (b.heat_score || 0) - (a.heat_score || 0))[0];
                  if (!trendingPoll) return null;
                  const sentimentShift = Math.min(99, Math.max(5, Math.floor((trendingPoll.heat_score || 0) * 0.85)));
                  return (
                    <div className="p-7 bg-gray-900 rounded-3xl border border-gray-800 shadow-2xl relative overflow-hidden text-white group">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-secondary-600/20 blur-[60px] rounded-full group-hover:scale-150 transition-transform duration-700"></div>
                      <h4 className="text-2xs font-black text-secondary-600 uppercase tracking-[0.3em] mb-5">Velocity Alert</h4>
                      <p className="font-bold text-base leading-tight mb-5 italic line-clamp-2 pr-2">"{trendingPoll.question}"</p>
                      <div className="flex items-center justify-between text-2xs font-black uppercase text-gray-500 mb-2">
                        <span>Sentiment Shift</span>
                        <span className="text-secondary-600">+{sentimentShift}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-secondary-600 animate-pulse shadow-[0_0_10px_#DB2777]" style={{ width: `${sentimentShift}%` }}></div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </section>
          </div>
        </aside>
      </div >

      {/* OVERLAYS */}
      {/* OVERLAYS */}
      {isSearchOpen && <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} polls={feedPolls} onSelectPoll={setPollToView} onCreate={handleCreateClick} votedPolls={votedPolls} />}
      {isCreateModalOpen && (
        <CreatePollModal 
          onCreate={handleCreatePoll} 
          onClose={() => setIsCreateModalOpen(false)} 
          isVerified={isVerified}
          onVerify={() => {
            // Keep isCreateModalOpen true so completeEnrollment knows to come back
            setIsEnrollmentOpen(true);
          }}
        />
      )}
      {isEnrollmentOpen && (
        <EnrollmentModal 
          onComplete={completeEnrollment} 
          onClose={() => setIsEnrollmentOpen(false)} 
          pollId={pendingVote?.pollId}
        />
      )}
      {insightsModalPoll && <PollInsightsModal poll={insightsModalPoll} onClose={() => setInsightsModalPoll(null)} />}
      {isRewardsModalOpen && <RewardsModal isOpen={isRewardsModalOpen} onClose={() => setIsRewardsModalOpen(false)} ipBalance={ipBalance} streak={streak} darkMode={darkMode} anonymousId={anonymousId} onBalanceUpdate={(verified) => { setIpBalance(verified); }} votedPollsList={votedPollsList} onGoVote={() => { setIsRewardsModalOpen(false); setCurrentScreen('feed'); }} />}
      {isHistoryModalOpen && <HistoryModal isOpen={isHistoryModalOpen} onClose={() => setIsHistoryModalOpen(false)} onSelectPoll={setPollToView} darkMode={darkMode} history={votedPollsList.map(p => ({ ...p, title: p.question, action: 'Voted', date: timeAgo(p.voted_at) }))} />}
      {
        pollToView && (
          <PollViewModal
            poll={pollToView}
            initialSelection={pollToViewSelection}
            isOpen={!!pollToView}
            onClose={() => { setPollToView(null); setPollToViewSelection(null); }}
            onVote={handleVote}
            votedPolls={votedPolls}
            darkMode={darkMode}
            anonymousId={anonymousId}
            onNext={handleNextPoll}
            onPrev={handlePrevPoll}
            pollExposure={pollExposure}
            registerVisibility={registerVisibility}
            onViewInsights={setInsightsModalPoll}
            isSaved={savedPollsList.some(p => p.id === pollToView.id)}
            onBookmarkToggle={handleBookmarkToggle}
            hasPrev={(() => {
              let activePolls = [];
              if (currentScreen === 'feed') activePolls = filteredPolls;
              else if (currentScreen === 'voted') activePolls = votedPollsList;
              else if (currentScreen === 'my_polls') activePolls = myPollsList;
              else if (currentScreen === 'saved') activePolls = savedPollsList;
              else activePolls = filteredPolls;
              return activePolls.findIndex(p => p.id === pollToView.id) > 0;
            })()}
            hasNext={(() => {
              let activePolls = [];
              if (currentScreen === 'feed') activePolls = filteredPolls;
              else if (currentScreen === 'voted') activePolls = votedPollsList;
              else if (currentScreen === 'my_polls') activePolls = myPollsList;
              else if (currentScreen === 'saved') activePolls = savedPollsList;
              else activePolls = filteredPolls;
              const idx = activePolls.findIndex(p => p.id === pollToView.id);
              return idx !== -1 && idx < activePolls.length - 1;
            })()}
          />
        )
      }
      {/* MOBILE/TABLET PULSE TRIGGER BAR — stays visible through the 1024-1279px range where
          the desktop right sidebar (xl:flex, below) isn't shown yet, so Global Pulse / Recent
          Activity is never completely unreachable at any viewport width. */}
      <button
        onClick={() => setIsPulseDrawerOpen(true)}
        className="xl:hidden fixed right-0 top-1/2 -translate-y-1/2 z-[100] group flex items-center h-20"
        aria-label="Open Network Pulse"
      >
        <div className="w-1.5 h-full bg-primary-600/40 hover:bg-primary-600 rounded-l-full transition-all group-hover:w-2.5 shadow-lg flex items-center justify-center overflow-hidden">
            <div className="w-1 h-1 bg-green-400 rounded-full animate-ping mr-0.5" />
        </div>
      </button>

      <GlobalPulseDrawer 
        isOpen={isPulseDrawerOpen}
        onClose={() => setIsPulseDrawerOpen(false)}
        darkMode={darkMode}
        onlineTotal={onlineTotal}
        recentActivity={votedPollsList}
        isVerified={isVerified}
        ipBalance={ipBalance}
        streak={streak}
        onViewActivity={setPollToView}
      />

      <NotificationDrawer
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        notifications={notifications}
        onMarkRead={handleMarkNotificationRead}
        onClearAll={handleClearAllNotifications}
      />

      {/* FLOATING ACTION BUTTON */}
      <button
        onClick={() => { haptics.impact(); handleCreateClick(); }}
        aria-label="Create a new poll"
        className="fixed bottom-28 lg:bottom-10 right-6 md:right-10 w-14 md:w-16 h-14 md:h-16 bg-primary-600 text-white rounded-card shadow-2xl flex items-center justify-center hover:scale-110 active:scale-90 transition-all z-[110] group"
      >
        <Plus className="w-6 h-6 md:w-8 md:h-8 group-hover:rotate-90 transition-transform duration-500" />
      </button>

      {/* MOBILE BOTTOM NAVIGATION (Full-width Tabs) */}
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 w-full h-[72px] border-t backdrop-blur-2xl z-[120] flex items-center justify-around px-2 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] transition-all duration-300 ${darkMode ? 'bg-gray-900/95 border-white/10' : 'bg-white/95 border-gray-100'}`}>
        <button onClick={() => setCurrentScreen('feed')} className="flex flex-col items-center gap-1 min-w-[60px] relative">
          <div className={`p-2 rounded-xl transition-all ${currentScreen === 'feed' ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-500'}`}>
            <Home size={20} />
          </div>
          <span className={`text-2xs font-black uppercase tracking-tighter ${currentScreen === 'feed' ? 'text-primary-600' : 'text-gray-500'}`}>Feed</span>
        </button>
        
        <button onClick={() => setCurrentScreen('saved')} className="flex flex-col items-center gap-1 min-w-[60px] relative">
          <div className={`p-2 rounded-xl transition-all ${currentScreen === 'saved' ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-500'}`}>
            <Bookmark size={20} />
            {!isVerified && <Lock size={8} className="absolute top-1 right-1 text-amber-500" />}
          </div>
          <span className={`text-2xs font-black uppercase tracking-tighter ${currentScreen === 'saved' ? 'text-primary-600' : 'text-gray-500'}`}>Saved</span>
        </button>

        <button onClick={() => setCurrentScreen('voted')} className="flex flex-col items-center gap-1 min-w-[60px] relative">
          <div className={`p-2 rounded-xl transition-all ${currentScreen === 'voted' ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-500'}`}>
            <CheckCircle2 size={20} />
            {!isVerified && <Lock size={8} className="absolute top-1 right-1 text-amber-500" />}
          </div>
          <span className={`text-2xs font-black uppercase tracking-tighter ${currentScreen === 'voted' ? 'text-primary-600' : 'text-gray-500'}`}>Voted</span>
        </button>

        <button onClick={() => setCurrentScreen('my_polls')} className="flex flex-col items-center gap-1 min-w-[60px] relative">
          <div className={`p-2 rounded-xl transition-all ${currentScreen === 'my_polls' ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-500'}`}>
            <Plus size={20} />
            {!isVerified && <Lock size={8} className="absolute top-1 right-1 text-amber-500" />}
          </div>
          <span className={`text-2xs font-black uppercase tracking-tighter ${currentScreen === 'my_polls' ? 'text-primary-600' : 'text-gray-500'}`}>My Polls</span>
        </button>

        <button onClick={() => setIsNotificationOpen(true)} aria-label={`Alerts${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`} className="flex flex-col items-center gap-1 min-w-[60px] relative">
          <div className={`p-2 rounded-xl transition-all ${isNotificationOpen ? 'bg-primary-600 text-white shadow-lg' : 'text-gray-500'}`}>
            <Bell size={20} />
            {unreadCount > 0 && <span className="absolute top-2 right-2 w-2 h-2 bg-secondary-600 rounded-full border border-white dark:border-gray-900"></span>}
          </div>
          <span className={`text-2xs font-black uppercase tracking-tighter ${isNotificationOpen ? 'text-primary-600' : 'text-gray-500'}`}>Alerts</span>
        </button>
      </nav>
    </div >
  );
}







/** * SCREEN 6: SAVED POLLS
 */
function SavedPollsScreen({ onSelectPoll, onViewInsights, allPolls, setSavedPollsList, votedPolls, onVote, anonymousId, pollExposure, pollUpdates, registerVisibility, savedPollsList, onBookmarkToggle }) {
  const [savedPolls, setSavedPolls] = useState([]);

  useEffect(() => {
    // Instead of localStorage, use the globally managed savedPollsList
    setSavedPolls(savedPollsList || []);
  }, [savedPollsList]);

  if (savedPolls.length === 0) {
    return (
      <div className="space-y-10 animate-in fade-in duration-700 text-center py-20">
        <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white mb-6">Saved Polls</h2>
        <div className="p-12 bg-white dark:bg-gray-800 rounded-3xl border border-dashed border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 font-bold italic">You haven't bookmarked any polls yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white">Saved Polls</h2>
      <div className="grid grid-cols-1 gap-4">
        {savedPolls.map(p => (
          <PollCard
            key={p.id}
            id={p.id}
            voted={votedPolls[p.id] === 'revealed'}
            onVote={onVote}
            onViewInsights={() => onViewInsights(p)}
            question={p.question}
            category={p.category}
            isPrivate={p.is_trap}
            type={p.poll_type}
            img={p.background_url}
            options={p.options}
            totalVoters={p.total_voters || 0}
            expires_at={p.expires_at}
            anonymousId={anonymousId}
            creator_id={p.creator_id}
            view_count={p.view_count || 0}
            pollExposure={pollExposure[p.id] || 0}
            pollUpdate={pollUpdates?.[p.id]}
            registerVisibility={registerVisibility}
            isSaved={savedPollsList?.some(saved => saved.id === p.id) || true}
            onBookmarkToggle={onBookmarkToggle}
            onCardClick={() => onSelectPoll({ ...p, isCreator: false })}
          />
        ))}
      </div>
    </div>
  );
}

/** * SCREEN 7: MY POLLS
 */
function MyPollsScreen({ onSelectPoll, onViewInsights, onCreate, anonymousId, allPolls, setMyPollsList, votedPolls, onVote, pollExposure, pollUpdates, registerVisibility, savedPollsList, onBookmarkToggle }) {
  const [myPolls, setMyPolls] = useState([]);

  useEffect(() => {
    if (!anonymousId) return;
    const filtered = allPolls.filter(p => p.creator_id === anonymousId);
    setMyPolls(filtered);
    // Don't overwrite the global list here, as it might empty it out momentarily
  }, [allPolls, anonymousId]);

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <div className="flex justify-between items-end">
        <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white">My Polls</h2>
        <button onClick={onCreate} className="px-6 py-3 bg-primary-600 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:scale-105 transition-transform shadow-lg shadow-purple-900/20 flex items-center space-x-2">
          <Plus size={16} /> <span>Create New</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {myPolls.map(p => (
          <PollCard
            key={p.id}
            id={p.id}
            voted={true} // Creator always sees results
            onVote={onVote}
            onViewInsights={() => onViewInsights(p)}
            question={p.question}
            category={p.category}
            isPrivate={p.is_trap}
            type={p.poll_type}
            img={p.background_url}
            options={p.options}
            totalVoters={p.total_voters || 0}
            expires_at={p.expires_at}
            anonymousId={anonymousId}
            creator_id={p.creator_id}
            compact={false}
            pollExposure={pollExposure[p.id] || 0}
            pollUpdate={pollUpdates?.[p.id]}
            registerVisibility={registerVisibility}
            isSaved={savedPollsList?.some(saved => saved.id === p.id)}
            onBookmarkToggle={onBookmarkToggle}
            onCardClick={() => onSelectPoll({ ...p, isCreator: true })}
          />
        ))}
      </div>
    </div>
  );
}

/** * SCREEN: VOTED POLLS
 */
function VotedPollsScreen({ onSelectPoll, onViewInsights, anonymousId, onVote, votedMap, votedPollsList, setVotedPollsList, pollExposure, pollUpdates, registerVisibility, savedPollsList, onBookmarkToggle }) {
  const [loading, setLoading] = useState(!votedPollsList || votedPollsList.length === 0);

  useEffect(() => {
    const fetchVoted = async () => {
      if (!anonymousId) {
        setLoading(false);
        return;
      }
      try {
        // We use a local loading state but only if we really have no data
        if (!votedPollsList || votedPollsList.length === 0) setLoading(true);
        const data = await api.getVotedPolls(anonymousId);
        if (setVotedPollsList) setVotedPollsList(data);
      } catch (err) {
        console.error("Failed to fetch voted polls:", err);
      } finally {
        setLoading(false);
      }
    };
    
    // Always fetch on mount or identity change to ensure sync
    fetchVoted();
  }, [anonymousId]); // Only depends on identity to avoid infinite loops

  // Filter duplicate poll IDs (can happen during local optimistic updates vs backend sync)
  const pollsToDisplay = (votedPollsList || []).reduce((acc, current) => {
    const x = acc.find(item => item.id === current.id);
    if (!x) return acc.concat([current]);
    return acc;
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-primary-600/20 border-t-primary-600 rounded-full animate-spin mb-4"></div>
        <p className="text-xs font-black text-gray-500 uppercase tracking-widest">Retrieving History...</p>
      </div>
    );
  }


  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white">Voted History</h2>

      {pollsToDisplay.length === 0 ? (
        <div className="p-12 bg-white dark:bg-gray-800 rounded-[40px] border border-dashed border-gray-200 dark:border-gray-700 text-center">
          <p className="text-gray-500 font-bold italic">You haven't voted on any polls yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {pollsToDisplay.map(p => (
            <PollCard
              key={p.id}
              id={p.id}
              voted={true} // These polls are from the "voted" endpoint — always show results
              onVote={onVote}
              onViewInsights={() => onViewInsights(p)}
              question={p.question}
              category={p.category}
              isPrivate={p.is_trap}
              type={p.poll_type}
              img={p.background_url}
              options={p.options}
              totalVoters={p.total_voters || 0}
              expires_at={p.expires_at}
              anonymousId={anonymousId}
              creator_id={p.creator_id}
              view_count={p.view_count || 0}
              pollExposure={pollExposure[p.id] || 0}
              pollUpdate={pollUpdates?.[p.id]}
              registerVisibility={registerVisibility}
              isSaved={savedPollsList?.some(saved => saved.id === p.id)}
              onBookmarkToggle={onBookmarkToggle}
              onCardClick={() => onSelectPoll({ ...p, isCreator: false })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** * WIDGET: HISTORY
 */
function HistoryWidget({ record, onClick, darkMode }) {
  return (
    <div
      onClick={() => onClick(record)}
      className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-all border border-transparent ${darkMode ? 'hover:bg-gray-800 hover:border-gray-700' : 'hover:bg-gray-50 hover:border-gray-100'
        } group`}
    >
      <span className="text-lg group-hover:scale-110 transition-transform duration-300">{record.icon}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] font-bold leading-tight truncate transition-colors ${darkMode ? 'text-gray-200 group-hover:text-white' : 'text-gray-900 group-hover:text-primary-600'
          }`}>
          {record.title}
        </p>
        <div className="flex items-center space-x-1.5 mt-0.5">
          <span className="text-2xs font-black uppercase tracking-widest text-gray-500">{record.action}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
          <span className="text-2xs font-black italic uppercase tracking-widest text-primary-600">{record.date}</span>
        </div>
      </div>
    </div>
  );
}

/** * SCREEN 9: PREFERENCES
 */
function SettingsScreen({ darkMode, setDarkMode, onWipeVault }) {
  const { showToast } = useToast();
  const [hapticsOn, setHapticsOn] = useState(() => isHapticsEnabled());
  const [notifStatus, setNotifStatus] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission; // 'granted' | 'denied' | 'default'
  });
  const [wipeArmed, setWipeArmed] = useState(false);

  const toggleHaptics = () => {
    const next = !hapticsOn;
    setHapticsEnabled(next);
    setHapticsOn(next);
    if (next) haptics.impact();
  };

  const toggleNotifications = async () => {
    if (typeof Notification === 'undefined') return;
    if (notifStatus === 'granted') {
      showToast("To turn notifications off, remove this site's permission in your browser settings.", 'info');
      return;
    }
    const result = await Notification.requestPermission();
    setNotifStatus(result);
    if (result === 'denied') {
      showToast('Notifications were blocked. You can re-enable them in your browser settings.', 'error');
    }
  };

  const handleWipeClick = () => {
    if (!wipeArmed) {
      setWipeArmed(true);
      setTimeout(() => setWipeArmed(false), 6000);
      return;
    }
    setWipeArmed(false);
    onWipeVault?.();
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700">
      <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none">Preferences</h2>

      <div className="space-y-8">
        <SettingsGroup title="System Architecture" darkMode={darkMode}>
          <SettingsToggle label="Dark Theme Mode" enabled={darkMode} onToggle={() => setDarkMode(!darkMode)} description="Switches the whole app to a dark palette." darkMode={darkMode} />
          <SettingsToggle
            label="Real-time Pulse Notifications"
            enabled={notifStatus === 'granted'}
            onToggle={toggleNotifications}
            disabled={notifStatus === 'unsupported'}
            description={notifStatus === 'unsupported' ? 'Not supported on this browser.' : notifStatus === 'denied' ? 'Blocked — re-enable in browser settings.' : 'Requires browser notification permission.'}
            darkMode={darkMode}
          />
          <SettingsToggle label="Enhanced Tactile Feedback" enabled={hapticsOn} onToggle={toggleHaptics} description="Vibrate on taps, votes, and confirmations." darkMode={darkMode} />
        </SettingsGroup >

        <SettingsGroup title="Vault & Privacy Control" darkMode={darkMode}>
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between p-7 bg-gray-50 dark:bg-gray-800/50 rounded-3xl border border-gray-100 dark:border-gray-700 group gap-4">
              <div className="flex-1">
                <h5 className="font-black text-gray-900 dark:text-white text-xl uppercase italic tracking-tighter leading-none mb-2">Local Encryption</h5>
                <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-2xs font-black uppercase tracking-widest border border-green-200/50 dark:border-green-500/20">Active</span>
              </div>
              <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-relaxed max-w-[200px] md:text-right">
                Your hardware hash is cryptographically secured.
              </p>
            </div>

            <button
              onClick={handleWipeClick}
              aria-label={wipeArmed ? 'Click again to permanently wipe your local Vault' : 'Terminate and wipe local Vault'}
              className={`w-full text-left p-7 border-2 rounded-3xl transition-all group flex items-center justify-between shadow-sm ${wipeArmed ? 'bg-red-600 border-red-600 text-white animate-pulse' : 'bg-white dark:bg-gray-900 border-red-50 dark:border-red-900/20 hover:bg-red-50 dark:hover:bg-red-900/10 text-red-600'}`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform ${wipeArmed ? 'bg-white/20 text-white' : 'bg-red-100 dark:bg-red-900/20 text-red-600'}`}>
                  <ShieldAlert size={28} />
                </div>
                <span className="font-black uppercase text-base tracking-tighter italic">
                  {wipeArmed ? 'Click again to confirm — cannot be undone' : 'Terminate & Wipe Local Vault'}
                </span>
              </div>
              <Trash2 className="w-6 h-6 opacity-40 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </SettingsGroup>
      </div>
    </div>
  );
}

function SettingsGroup({ title, children, darkMode }) {
  return (
    <div className={`rounded-panel border p-8 shadow-xl shadow-purple-900/5 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
      <h4 className="text-[11px] font-black text-gray-500 uppercase tracking-[0.4em] mb-8 border-b border-gray-50 pb-4">{title}</h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function SettingsToggle({ label, enabled, description, onToggle, darkMode, disabled = false }) {
  return (
    <div
      onClick={disabled ? undefined : onToggle}
      role="switch"
      aria-checked={enabled}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onToggle?.(); } }}
      className={`flex items-center justify-between p-6 border rounded-card transition-all group focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-primary-600 focus-visible:outline-offset-2 ${disabled ? 'opacity-60 cursor-not-allowed border-dashed border-gray-200 dark:border-gray-700' : 'cursor-pointer border-transparent hover:border-gray-100'} ${darkMode ? 'bg-gray-700 hover:bg-gray-600/50' : 'bg-white hover:bg-gray-50/50'}`}
    >
      <div>
        <span className={`font-black text-lg uppercase italic tracking-tighter leading-none block mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{label}</span>
        {description && <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">{description}</p>}
      </div>
      <div className={`w-14 h-7 rounded-full p-1.5 transition-all duration-300 shadow-inner shrink-0 ml-4 ${disabled ? 'bg-transparent border-2 border-dashed border-gray-300 dark:border-gray-600' : enabled ? 'bg-primary-600' : 'bg-gray-200'}`}>
        <div className={`w-4 h-4 rounded-full transition-all duration-300 shadow-md ${disabled ? 'bg-gray-300 dark:bg-gray-600' : 'bg-white'} ${enabled ? 'translate-x-7' : 'translate-x-0'}`}></div>
      </div>
    </div>
  );
}

/** * SCREEN 10: LEGAL & TRANSPARENCY
 */
function LegalScreen() {
  return (
    <div className="space-y-10 animate-in fade-in duration-700">
      <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white">Privacy</h2>

      <div className="space-y-10">
        <div className="bg-white dark:bg-gray-800 p-12 rounded-[40px] border border-gray-100 dark:border-gray-700 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-4 h-full bg-primary-600"></div>
          <h3 className="text-3xl font-black text-gray-900 dark:text-white uppercase italic mb-8 tracking-tighter">Anonymous & Private</h3>
          <p className="text-xl font-bold text-gray-500 dark:text-gray-400 leading-relaxed italic mb-8 border-l-8 border-gray-100 dark:border-gray-700 pl-8">"Your identity is not our business. No cookies. No tracking. No third-party scripts. Your data is private and encrypted."</p>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-500 leading-relaxed">Participation is anonymous. Once a poll hits the <span className="text-gray-900 dark:text-white font-black">25-vote threshold</span>, the results become part of the verified history for everyone to see.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LegalCard
            title="Privacy Architecture"
            body="Your identity is derived and encrypted on your device before it ever reaches our servers. We store a pseudonymous Ghost ID, not your name or email in plain text — the Diagnostics screen shows you exactly what's on file for your own account."
          />
          <LegalCard
            title="Terms of Record"
            body="Once a poll reaches 25 verified votes, its results become part of the permanent public record and can no longer be deleted or edited by the creator. Votes cast before that threshold remain visible only in aggregate."
          />
          <LegalCard
            title="AI Moderation Logic"
            body="The VANE engine screens votes for basic-attention and eligibility signals (like the calibration prompts you sometimes see before voting) to keep results representative — it never blocks a vote for the opinion expressed, only for signals of bot or low-effort activity."
          />
          <LegalCard
            title="Public Record Rights"
            body="You can request removal of your own historical votes by wiping your local Vault in Preferences. Aggregate poll results that have already crossed the 25-vote public threshold are retained as anonymous statistics, not tied back to any identity."
          />
        </div>

        <div className="p-8 bg-gray-900 dark:bg-black/50 rounded-3xl flex items-start space-x-6 border border-gray-800 shadow-2xl">
          <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="text-accent-500" size={32} />
          </div>
          <div>
            <h5 className="font-black uppercase text-sm text-white tracking-[0.2em] mb-2 italic">Community Oversight</h5>
            <p className="text-xs text-gray-500 font-bold leading-relaxed italic">Open Opinion employs decentralized governance auditing to ensure sentiment data remains raw, unbiased, and immune to tampering.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegalCard({ title, body }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={`bg-white dark:bg-gray-800 border rounded-card transition-all shadow-sm overflow-hidden ${isOpen ? 'border-primary-600' : 'border-gray-100 dark:border-gray-700'}`}>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        className="w-full p-7 flex items-center justify-between group cursor-pointer hover:border-primary-600 transition-all text-left"
      >
        <span className="font-black text-[15px] uppercase tracking-widest italic text-gray-900 dark:text-white">{title}</span>
        <div className={`w-10 h-10 shrink-0 ml-4 rounded-full border flex items-center justify-center transition-all ${isOpen ? 'text-primary-600 bg-primary-600/5 border-primary-600/20 rotate-90' : 'text-gray-200 dark:text-gray-600 border-gray-50 dark:border-gray-700 group-hover:text-primary-600 group-hover:bg-primary-600/5 group-hover:border-primary-600/20'}`}>
          <ArrowRight size={20} />
        </div>
      </button>
      {isOpen && (
        <p className="px-7 pb-7 text-sm font-medium text-gray-600 dark:text-gray-400 leading-relaxed animate-in fade-in slide-in-from-top-2 duration-300">
          {body}
        </p>
      )}
    </div>
  );
}

/** * SCREEN 8: NOTIFICATION DRAWER
 */
function NotificationDrawer({ isOpen, onClose, notifications, onMarkRead, onClearAll }) {
  const containerRef = useModalA11y(isOpen, onClose);
  const hasUnread = (notifications || []).some(n => !n.is_read);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[120]">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose}></div>
      <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Activity notifications" className="absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl animate-in slide-in-from-right duration-500 p-8 md:p-12 flex flex-col border-l border-white/20 dark:border-gray-800">
        <div className="flex justify-between items-center mb-12">
          <div className="flex flex-col">
            <h3 className="text-4xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white">Activity</h3>
            <span className="text-2xs font-black text-gray-500 uppercase tracking-[0.3em] mt-2">Real-time Stream</span>
          </div>
          <button onClick={onClose} aria-label="Close activity panel" className="p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all text-gray-900 dark:text-white">
            <X size={28} />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto no-scrollbar flex-1 pr-2">
          {(!notifications || notifications.length === 0) ? (
            <div className="p-8 bg-gray-50 dark:bg-gray-800/50 rounded-3xl border border-dashed border-gray-200 dark:border-gray-700 text-center">
              <p className="text-gray-500 font-bold italic text-sm">No new network events...</p>
            </div>
          ) : (
            notifications.map(n => (
              <NotificationItem 
                key={n.id} 
                notification={n} 
                onClick={() => {
                  if (!n.is_read) onMarkRead(n.id);
                  // Further routing could be done here based on n.reference_id
                }} 
              />
            ))
          )}
        </div>

        <div className="pt-8 border-t border-gray-100 dark:border-gray-800"></div>
        <button
          onClick={onClearAll}
          disabled={!hasUnread}
          className="w-full py-5 border-2 border-gray-100 dark:border-gray-800 text-gray-500 rounded-modal font-black uppercase text-xs tracking-[0.4em] hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          {hasUnread ? 'Clear Stream' : 'All Caught Up'}
        </button>
      </div>
    </div>
  );
}

function NotificationItem({ notification, onClick }) {
  // Map internal types to visual icons/colors
  let visualType = 'system';
  if (notification.type === 'POLL_CREATED' || notification.type === 'POLL_GLOBAL') visualType = 'milestone';
  if (notification.type === 'POLL_MILESTONE' || notification.type === 'POLL_EXPLICIT_EXPIRY' || notification.type === 'POLL_EXPIRED') visualType = 'velocity';
  if (notification.type === 'STREAK_EXPIRING' || notification.type === 'EDUCATIONAL_COINS') visualType = 'royalty';

  const icons = {
    milestone: <Globe size={24} />,
    velocity: <Flame size={24} />,
    royalty: <Wallet size={24} />,
    system: <ShieldCheck size={24} />
  };
  const iconColors = {
    milestone: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    velocity: 'bg-pink-50 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
    royalty: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    system: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
  };
  
  const isUnread = !notification.is_read;

  return (
    <div onClick={onClick} className={`flex items-start space-x-6 p-4 rounded-2xl cursor-pointer transition-all ${isUnread ? 'bg-gray-50 dark:bg-gray-800/80 border border-primary-600/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40 border border-transparent'} group`}>
      <div className={`w-14 h-14 rounded-[18px] flex flex-shrink-0 items-center justify-center shadow-inner group-hover:scale-110 transition-transform ${iconColors[visualType]}`}>
        {icons[visualType]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center mb-1">
          <h4 className={`font-black text-base uppercase tracking-tight italic transition-colors ${isUnread ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'} group-hover:text-primary-600`}>
            {notification.title}
          </h4>
          {isUnread && <span className="w-2 h-2 bg-primary-600 rounded-full"></span>}
        </div>
        <p className={`text-xs font-bold leading-relaxed italic pr-4 ${isUnread ? 'text-gray-600 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}`}>
          {notification.message}
        </p>
      </div>
    </div>
  );
}



